/**
 * NOS-124: Media library video list (Published / Drafts).
 */
import { Link } from "react-router-dom"
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Badge } from "@/components/ui/badge"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { MediaSectionNav } from "@/components/dashboard/MediaSectionNav"
import { PermissionGate } from "@/components/PermissionGate"
import { Navigate } from "react-router-dom"
import { useOrgStore } from "@/stores/orgStore"
import { formatDate } from "@/lib/format"
import {
  useMediaVideos,
  useDeleteMediaVideo,
  speakerNames,
  type MediaTab,
} from "@/hooks/useMediaVideos"
import { toast } from "sonner"
import { supabase } from "@/lib/supabase"
import { getPodcastFeedUrl } from "@/lib/podcastFeedUrl"
import { ExternalLink } from "lucide-react"
import { useEffect, useState } from "react"

function TranscriptStatusBadge({ status }: { status: string }) {
  const s = status ?? "none"
  if (s === "pending") {
    return (
      <Badge variant="outline" className="gap-1 border-amber-500/50 text-amber-700">
        <Loader2 className="h-3 w-3 animate-spin" />
        pending
      </Badge>
    )
  }
  if (s === "complete")
    return (
      <Badge variant="default" className="bg-emerald-600 hover:bg-emerald-600">
        transcript
      </Badge>
    )
  if (s === "failed")
    return <Badge variant="destructive">transcript failed</Badge>
  return <Badge variant="secondary">no transcript</Badge>
}

function AiStatusBadge({ status }: { status: string }) {
  const s = status ?? "none"
  if (s === "pending") {
    return (
      <Badge variant="outline" className="gap-1 border-amber-500/50 text-amber-700">
        <Loader2 className="h-3 w-3 animate-spin" />
        AI…
      </Badge>
    )
  }
  if (s === "complete") return <Badge className="bg-violet-600 hover:bg-violet-600">AI ready</Badge>
  if (s === "failed") return <Badge variant="destructive">AI failed</Badge>
  return <Badge variant="secondary">no AI</Badge>
}

export function MediaPage() {
  const { currentOrg } = useOrgStore()
  const orgId = currentOrg?.id ?? null
  const orgSlug = currentOrg?.slug ?? ""
  const [tab, setTab] = useState<MediaTab>("published")
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [anyPublishedAudio, setAnyPublishedAudio] = useState<boolean | null>(null)

  const podcastConfigured = Boolean(
    currentOrg &&
      currentOrg.podcast_settings &&
      typeof currentOrg.podcast_settings === "object" &&
      !Array.isArray(currentOrg.podcast_settings) &&
      Object.keys(currentOrg.podcast_settings as object).length > 0
  )
  const feedUrl = orgSlug && podcastConfigured ? getPodcastFeedUrl(orgSlug) : ""

  useEffect(() => {
    let cancelled = false
    async function checkAudio() {
      if (!orgId) {
        setAnyPublishedAudio(null)
        return
      }
      const now = new Date().toISOString()
      const { count } = await supabase
        .from("media_videos")
        .select("id", { count: "exact", head: true })
        .eq("org_id", orgId)
        .eq("is_published", true)
        .not("audio_url", "is", null)
        .or(`published_at.is.null,published_at.lte.${now}`)
      if (!cancelled) setAnyPublishedAudio((count ?? 0) > 0)
    }
    void checkAudio()
    return () => {
      cancelled = true
    }
  }, [orgId])

  const { videos, loading, error, refresh } = useMediaVideos(orgId, tab)
  const { deleteMediaVideo } = useDeleteMediaVideo(orgId)

  const handleDelete = async () => {
    if (!deleteId) return
    setDeleting(true)
    const ok = await deleteMediaVideo(deleteId)
    setDeleting(false)
    setDeleteId(null)
    if (ok) {
      toast.success("Video deleted")
      refresh()
    } else {
      toast.error("Failed to delete video")
    }
  }

  const thumbUrl = (youtubeId: string | null, thumb: string | null) =>
    thumb || (youtubeId ? `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg` : "")

  return (
    <PermissionGate permission="can_manage_media" fallback={<Navigate to="/dashboard" replace />}>
      <div className="min-w-0 space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold">Media</h1>
            <p className="text-muted-foreground">Sermons and video library for your site.</p>
          </div>
          <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
            {feedUrl ? (
              <Button variant="outline" size="sm" asChild>
                <a href={feedUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1">
                  <ExternalLink className="h-4 w-4" />
                  Subscribe to Podcast (RSS)
                </a>
              </Button>
            ) : null}
            <Button asChild>
              <Link to="/dashboard/media/new">
                <Plus className="mr-2 h-4 w-4" />
                New video
              </Link>
            </Button>
          </div>
        </div>
        <MediaSectionNav />

        {feedUrl && anyPublishedAudio === false && (
          <p className="text-sm text-amber-800 dark:text-amber-200 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2">
            Upload audio (MP3) on published videos to enable the best experience for Apple Podcasts and Spotify.
          </p>
        )}


        <Tabs value={tab} onValueChange={(v) => setTab(v as MediaTab)}>
          <TabsList>
            <TabsTrigger value="published">Published</TabsTrigger>
            <TabsTrigger value="drafts">Drafts</TabsTrigger>
          </TabsList>
        </Tabs>

        {error && (
          <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : videos.length === 0 ? (
          <div className="rounded-lg border border-dashed py-16 text-center">
            <p className="text-muted-foreground">No videos in this tab yet.</p>
            <Button asChild variant="outline" className="mt-4">
              <Link to="/dashboard/media/new">Add a video</Link>
            </Button>
          </div>
        ) : (
          <div className="min-w-0 overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]" />
                  <TableHead>Title</TableHead>
                  <TableHead>Series</TableHead>
                  <TableHead>Speakers</TableHead>
                  <TableHead>Published</TableHead>
                  <TableHead>Transcript</TableHead>
                  <TableHead>AI</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {videos.map((v) => (
                  <TableRow key={v.id}>
                    <TableCell>
                      {thumbUrl(v.youtube_video_id, v.thumbnail_url) ? (
                        <img
                          src={thumbUrl(v.youtube_video_id, v.thumbnail_url)}
                          alt=""
                          className="h-14 w-24 rounded object-cover"
                        />
                      ) : (
                        <div className="h-14 w-24 rounded bg-muted" />
                      )}
                    </TableCell>
                    <TableCell className="font-medium">
                      <Link to={`/dashboard/media/${v.id}/edit`} className="hover:underline">
                        {v.title}
                      </Link>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {v.media_series?.title ?? "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{speakerNames(v) || "—"}</TableCell>
                    <TableCell className="text-muted-foreground">{formatDate(v.published_at)}</TableCell>
                    <TableCell>
                      <TranscriptStatusBadge status={v.transcript_status} />
                    </TableCell>
                    <TableCell>
                      <AiStatusBadge status={v.ai_content_status} />
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="icon" asChild>
                          <Link to={`/dashboard/media/${v.id}/edit`}>
                            <Pencil className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteId(v.id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete video?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PermissionGate>
  )
}
